import { ArrowRight, BarChart3, Phone, CalendarCheck, DollarSign, MousePointer, Lock } from 'lucide-react';

const STAGES = [
  { icon: MousePointer, label: 'Google Ads', sublabel: 'Clicks & Impressions', color: 'text-[var(--ed-t2)]', active: true },
  { icon: BarChart3, label: 'GA4 Events', sublabel: 'Site Behavior', color: 'text-[var(--ed-t3)]', active: false },
  { icon: Phone, label: 'Form Fills & Calls', sublabel: 'Lead Capture', color: 'text-[var(--ed-t3)]', active: false },
  { icon: CalendarCheck, label: 'Reservations', sublabel: 'Bookings Made', color: 'text-[var(--ed-amber)]', active: false },
  { icon: DollarSign, label: 'Revenue', sublabel: 'Actual Revenue', color: 'text-[var(--ed-green)]', active: false },
];

const VALUE_PROPS = [
  { title: 'True Cost-Per-Reservation', description: 'See the real cost of each booking, not just clicks' },
  { title: 'Revenue Attribution', description: 'Know which campaigns drive the highest-value guests' },
  { title: 'Cross-Channel Visibility', description: 'Connect Google Ads, GA4, forms, calls, and your POS' },
];

export function FullFunnelTab() {
  return (
    <div className="space-y-10">
      <div className="text-center py-8">
        <h2 className="text-xl font-display font-light text-foreground mb-2">Full Funnel Intelligence</h2>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">
          See exactly which Google Ads campaigns drive the most profitable guests —
          from first click to final check.
        </p>
      </div>

      {/* Funnel visualization */}
      <div className="flex flex-wrap items-center justify-center gap-2 px-4 sm:px-8">
        {STAGES.map((stage, i) => {
          const Icon = stage.icon;
          return (
            <div key={stage.label} className="flex items-center gap-2">
              <div className={`ed-card p-4 text-center min-w-[140px] relative ${
                stage.active ? 'border-[var(--ed-rule-heavy)]' : ''
              }`}>
                {stage.active && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[0.5rem] px-1.5 py-0.5 rounded-full bg-[var(--ed-green-bg)] text-[var(--ed-green)] border border-[rgba(74,186,122,0.12)] font-medium">
                    Active
                  </span>
                )}
                <Icon className={`h-6 w-6 mx-auto mb-2 ${stage.color} ${stage.active ? 'opacity-80' : 'opacity-30'}`} />
                <div className={`text-sm font-medium ${stage.active ? 'text-foreground/80' : 'text-foreground/30'}`}>{stage.label}</div>
                <div className="text-[0.6rem] text-muted-foreground/40 mt-0.5">{stage.sublabel}</div>
              </div>
              {i < STAGES.length - 1 && (
                <ArrowRight className={`h-4 w-4 flex-shrink-0 ${
                  stage.active ? 'text-muted-foreground/40' : 'text-muted-foreground/15'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Value props */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
        {VALUE_PROPS.map((prop) => (
          <div key={prop.title} className="ed-card p-4 text-center">
            <p className="text-sm font-medium text-foreground/80 mb-1">{prop.title}</p>
            <p className="text-[0.7rem] text-muted-foreground/60 leading-relaxed">{prop.description}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="ed-card p-8 text-center max-w-lg mx-auto">
        <Lock className="h-5 w-5 mx-auto mb-3 text-muted-foreground/40" />
        <p className="text-sm text-[var(--ed-t1)] font-medium mb-1">Available with Full Engagement Package</p>
        <p className="text-xs text-muted-foreground leading-relaxed mb-4">
          Connect Google Ads with your reservation system, GA4, and CRM
          to unlock true cost-per-reservation and revenue attribution per campaign.
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.04] border border-[var(--ed-rule)] text-[0.75rem] text-[var(--ed-t2)]">
          Ask your account manager to activate
        </div>
      </div>
    </div>
  );
}
